import classNames from "classnames";
import { RefObject } from "react";
import * as Select from "@radix-ui/react-select";
import {
  Popover,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from "~/components/shared/PopoverNext";
import Icon from "~/components/shared/Icon";
import { Tooltip } from "~/components/shared/Tooltip";
import { useTrack } from "~/components/build/Show/lib/useTrack";

import {
  DisplayMode,
  useFilterStore,
  useStateCounts,
} from "~/components/build/Show/lib/useFilterStore";
import { FilterByOption } from "~/components/build/Show/lib/filterConstants";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { useToggleSkippedSteps } from "~/components/build/Show/lib/useToggleSkippedSteps";
import Count from "~/components/build/Show/components/Count";

const options = {
  [DisplayMode.Pipeline]: "None",
  [DisplayMode.State]: "State",
};

const displayModeOrder = [DisplayMode.Pipeline, DisplayMode.State];

export default function DisplayOptions(props: { collisionBoundaryRef: RefObject<Element | null> }) {
  const displayMode = useFilterStore((state) => state.displayMode);
  const setDisplayMode = useFilterStore((state) => state.setDisplayMode);
  const isDisplayOptionsOpen = useBuildPageStore((state) => state.isDisplayOptionsOpen);
  const setDisplayOptionsOpen = useBuildPageStore((state) => state.setDisplayOptionsOpen);
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);
  const showRetriedJobs = useBuildPreferencesStore((state) => state.showRetriedJobs);
  const setShowRetriedJobs = useBuildPreferencesStore((state) => state.setShowRetriedJobs);
  const toggleSkippedSteps = useToggleSkippedSteps();
  const counts = useStateCounts();
  const skippedCount = counts[FilterByOption.Ignored];
  const track = useTrack();

  return (
    <Popover open={isDisplayOptionsOpen} onOpenChange={setDisplayOptionsOpen}>
      <div className="flex">
        <Tooltip tooltip="Show display options" placement="bottom" disabled={isDisplayOptionsOpen}>
          <PopoverTrigger asChild={true}>
            <button
              data-testid="display-mode-dropdown"
              className="right-tooltip hover:bg-text-600 group inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 font-medium transition-all hover:bg-purple-100 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50 aria-checked:bg-purple-100 aria-checked:text-purple-600"
              aria-checked={isDisplayOptionsOpen}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 7h-9" />
                <path d="M14 17H5" />
                <circle cx="17" cy="17" r="3" />
                <circle cx="7" cy="7" r="3" />
              </svg>
              <span className="sr-only">Show display options</span>
            </button>
          </PopoverTrigger>
        </Tooltip>
      </div>

      <PopoverPortal>
        <PopoverContent
          side="bottom"
          collisionBoundary={props.collisionBoundaryRef.current}
          collisionPadding={0}
          className="w-max min-w-[280px] p-3"
          data-testid="display-mode-dropdown-content"
        >
          <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2">
            {/* Group by */}
            <span className="text-charcoal-700 text-sm/7">Group by</span>
            <Select.Root
              value={displayMode ?? undefined}
              onValueChange={(value) => {
                track(`Group Sidebar Steps`, { group_by: value });
                setDisplayMode(value as DisplayMode);
              }}
            >
              <Select.Trigger
                aria-label="Group by"
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-400 bg-white px-3 py-0.5 outline-none"
              >
                <Select.Value />
                <Icon icon="down-triangle" className="size-[7px] shrink-0" />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="z-50 min-w-[120px]"
                  position="popper"
                  sideOffset={4}
                  align="end"
                >
                  <Select.Viewport className="rounded-md border border-gray-300 bg-white py-0.5 shadow-md">
                    {displayModeOrder.map((option) => (
                      <Select.Item
                        key={option}
                        value={option}
                        className="cursor-pointer px-3 py-1.5 text-left text-sm font-medium outline-none data-[highlighted]:bg-gray-100 data-[state=checked]:bg-purple-100 data-[highlighted]:text-purple-600 data-[state=checked]:text-purple-600"
                      >
                        <Select.ItemText>{options[option]}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>

            <hr className="col-span-2 -mx-3 my-0 border-gray-300" />

            {/* Toggle options */}
            {Features.BuildListJobRetryHistory && (
              <>
                <span className="text-charcoal-700 flex items-center gap-1.5 whitespace-nowrap text-sm/7">
                  Show past retry attempts
                </span>
                <button
                  role="switch"
                  aria-label="Show past retry attempts"
                  aria-checked={showRetriedJobs}
                  onClick={() => setShowRetriedJobs(!showRetriedJobs)}
                  className={classNames(
                    "relative inline-flex h-4 w-7 shrink-0 cursor-pointer justify-self-end rounded-full border-2 border-transparent transition-colors",
                    showRetriedJobs ? "bg-purple-600" : "bg-gray-300",
                  )}
                >
                  <span
                    className={classNames(
                      "pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                      showRetriedJobs ? "translate-x-3" : "translate-x-0",
                    )}
                  />
                </button>
              </>
            )}

            <span className="text-charcoal-700 flex items-center gap-1.5 whitespace-nowrap text-sm/7">
              Show skipped steps
              {skippedCount > 0 && <Count>{skippedCount}</Count>}
            </span>
            <button
              role="switch"
              aria-label="Show skipped steps"
              aria-checked={showSkippedSteps}
              onClick={toggleSkippedSteps}
              className={classNames(
                "relative inline-flex h-4 w-7 shrink-0 cursor-pointer justify-self-end rounded-full border-2 border-transparent transition-colors",
                showSkippedSteps ? "bg-purple-600" : "bg-gray-300",
              )}
            >
              <span
                className={classNames(
                  "pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                  showSkippedSteps ? "translate-x-3" : "translate-x-0",
                )}
              />
            </button>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}
